import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ExternalLink } from 'lucide-react';
import { AppIdea } from '@/hooks/app-library';
import { Badge } from '@/components/ui/badge';

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <div className="space-y-4">
    <h2 className="text-lg font-semibold text-foreground border-b border-border pb-2">
      {title}
    </h2>
    <div className="text-muted-foreground leading-relaxed">
      {children}
    </div>
  </div>
);

interface MarkdownContentProps {
  content: string;
  citations: string[];
}

const MarkdownContent: React.FC<MarkdownContentProps> = ({ content, citations }) => {
  // Preprocess content to convert citations [1], [2], etc. into markdown links
  const preprocessContent = (contentText: string): string => {
    if (!contentText || !citations || citations.length === 0) {
      return contentText;
    }

    // Replace citation patterns [1], [2], etc. with markdown links
    return contentText.replace(/\[(\d+)\]/g, (match, citationNumber) => {
      const index = parseInt(citationNumber) - 1;
      if (index >= 0 && index < citations.length) {
        const citation = citations[index];
        return `[${citationNumber}](${citation})`;
      }
      return match; // Return original if no matching citation
    });
  };

  const processedContent = preprocessContent(content);

  return (
    <div className="prose prose-lg max-w-none dark:prose-invert overflow-hidden break-words">
      <style>
        {`
        .prose {
          max-width: none !important;
          width: 100%;
          overflow-wrap: break-word;
          word-wrap: break-word;
        }
        .prose * {
          max-width: 100%;
          overflow-wrap: break-word;
          word-wrap: break-word;
        }
        .prose pre {
          max-width: 100%;
          overflow-x: auto;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
        .prose code {
          word-break: break-all;
        }
        .prose table {
          max-width: 100%;
          table-layout: fixed;
          overflow-x: auto;
          display: block;
          white-space: nowrap;
        }
        .prose img {
          max-width: 100%;
          height: auto;
        }
        .prose a[href*="http"]:not([href*="#"]) {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          height: 20px;
          padding: 2px 6px;
          margin: 0 2px;
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 12px;
          color: rgb(59, 130, 246);
          text-decoration: none;
          font-size: 12px;
          font-weight: 500;
          transition: all 0.2s ease;
          word-break: normal;
          flex-shrink: 0;
        }
        .prose a[href*="http"]:not([href*="#"]):hover {
          background: rgba(59, 130, 246, 0.2);
          border-color: rgba(59, 130, 246, 0.5);
          transform: scale(1.05);
        }
        .dark .prose a[href*="http"]:not([href*="#"]) {
          background: rgba(59, 130, 246, 0.2);
          border-color: rgba(59, 130, 246, 0.4);
          color: rgb(147, 197, 253);
        }
        .dark .prose a[href*="http"]:not([href*="#"]):hover {
          background: rgba(59, 130, 246, 0.3);
          border-color: rgba(59, 130, 246, 0.6);
        }
        `}
      </style>
      
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children, ...props }) => {
            // Handle citation links
            if (href && href.startsWith('http') && typeof children === 'string' && /^\d+$/.test(children)) {
              const citationNumber = parseInt(children);
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(href, '_blank', 'noopener,noreferrer');
                  }}
                  {...props}
                >
                  {children}
                </a>
              );
            }
            // Handle regular links
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                {...props}
              >
                {children}
              </a>
            );
          }
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

// Sources Section Component
const SourcesSection: React.FC<{ citations: string[] }> = ({ citations }) => {
  if (!citations || citations.length === 0) return null;

  const truncateUrl = (url: string, maxLength: number = 50) => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };

  return (
    <Section title="Sources">
      <div className="flex flex-wrap gap-2 w-full overflow-hidden">
        {citations.map((citation, index) => (
          <button
            key={citation}
            onClick={() => window.open(citation, '_blank', 'noopener,noreferrer')}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 dark:from-blue-400/20 dark:to-cyan-400/20 backdrop-blur-sm border border-blue-200/30 dark:border-blue-400/30 rounded-full text-xs font-medium text-blue-700 dark:text-blue-300 hover:from-blue-500/20 hover:to-cyan-500/20 dark:hover:from-blue-400/30 dark:hover:to-cyan-400/30 transition-all duration-200 group cursor-pointer max-w-[280px] md:max-w-[320px]"
            title={citation}
          >
            <span className="font-semibold flex-shrink-0">[{index + 1}]</span>
            <span className="truncate max-w-[200px] md:max-w-[240px]">
              {truncateUrl(citation, 40)}
            </span>
            <ExternalLink className="h-3 w-3 opacity-70 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </button>
        ))}
      </div>
    </Section>
  );
};

export const AppIdeaResearchSection: React.FC<{ idea: AppIdea }> = ({ idea }) => {
  console.log('[AppIdeaResearchSection] Rendering with idea:', {
    id: idea.id,
    name: idea.name || idea.generated_title,
    hasResearchOverview: !!idea.research_overview,
    hasResearchRequirements: !!idea.research_requirements,
    hasResearchFeatures: !!idea.research_enhanced_features,
    hasCitations: !!idea.research_citations,
    citationsCount: idea.research_citations?.length || 0,
    citations: idea.research_citations,
    hasRelatedQuestions: !!idea.research_related_questions,
    relatedQuestionsCount: idea.research_related_questions?.length || 0,
    researchOverviewLength: idea.research_overview?.length || 0,
    researchRequirementsLength: idea.research_requirements?.length || 0,
    researchFeaturesLength: idea.research_enhanced_features?.length || 0
  });

  const citations = idea.research_citations || [];

  // Check if any research data exists
  const hasAnyResearchData = idea.research_overview || idea.research_requirements || idea.research_enhanced_features || (citations && citations.length > 0);

  if (!hasAnyResearchData) {
    return (
      <div className="space-y-8">
        <Section title="Research Data">
          <div className="text-center py-8">
            <div className="text-muted-foreground">
              <p className="text-sm">No research data available for this app idea.</p>
              <p className="text-xs mt-2">Research may still be generating or this app idea was created without research.</p>
            </div>
          </div>
        </Section>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {idea.research_overview && (
        <Section title="Research Overview">
          <MarkdownContent content={idea.research_overview} citations={citations} />
        </Section>
      )}
      
      {idea.research_requirements && (
        <Section title="Requirements">
          <MarkdownContent content={idea.research_requirements} citations={citations} />
        </Section>
      )}
      
      {idea.research_enhanced_features && (
        <Section title="Enhanced Features">
          <MarkdownContent content={idea.research_enhanced_features} citations={citations} />
        </Section>
      )}
      
      {/* Sources Section - Moved to Bottom */}
      <SourcesSection citations={citations} />
    </div>
  );
};
